import * as React from "react";
import {useMutation} from "react-apollo";
import {noop} from "lodash";
import {ApolloError} from "apollo-client";

import useErrorReporting from "shared/General/components/ErrorReporting/useErrorReporting";

import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import {
    generateRecommendations,
    GenerateRecommendationsMutation,
    GenerateRecommendationsVariables,
} from "../../NextBestActions/NextBestActions";

export const RECOMMENDATION_PAGE_BATCH_SIZE = 6;
export const RECOMMENDATION_BANNER_BATCH_SIZE = 3;

type RecommendationContext = {
    getRecommendationsCallback: (batchSize: number) => Promise<void>;
    data?: GenerateRecommendationsMutation;
    loading: boolean;
    error: ApolloError;
    hasRecommendations: boolean;
};

export const RecommendationsContext = React.createContext<RecommendationContext>({
    getRecommendationsCallback: noop as (batchSize: number) => Promise<void>,
    data: null,
    loading: false,
    error: null,
    hasRecommendations: false,
});

export const RecommendationsProvider: React.FC = ({children}) => {
    const {id} = useUniversity();
    const {reportError} = useErrorReporting();
    const [hasRecommendations, setHasRecommendations] = React.useState<boolean>(false);
    const [getGeneratedRecommendations, {data, loading, error}] = useMutation<
        GenerateRecommendationsMutation,
        GenerateRecommendationsVariables
    >(generateRecommendations);

    async function GenerateRecommendations(batchSize: number) {
        try {
            const recommendations = await getGeneratedRecommendations({
                variables: {
                    universityId: id,
                    batchSize,
                },
            });
            if (!hasRecommendations && recommendations.data.generateRecommendations.length) {
                setHasRecommendations(true);
            }
        } catch (e) {
            reportError(e);
        }
    }

    const getRecommendationsCallback = React.useCallback(GenerateRecommendations, [id]);

    return (
        <RecommendationsContext.Provider
            value={{getRecommendationsCallback, data, loading, error, hasRecommendations}}
        >
            {children}
        </RecommendationsContext.Provider>
    );
};

export const useRecommendations = () =>
    React.useContext<RecommendationContext>(RecommendationsContext);
